import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, InlineError, Input } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { socialKeys } from '../socialStudio/queries';
import type { Task } from '../tasks/types';

/**
 * Pushing a project task into Social Studio.
 *
 * The mapping the database does — title, due date to publish date, assignee,
 * description to caption, attached links to asset links — needs no input. What
 * it cannot guess is the three fields a social post has and a task does not,
 * so those are all this dialog asks for.
 *
 * One RPC rather than an insert plus an update: the post and the backlink have
 * to land together, or the task offers a second push of something already
 * pushed.
 */

/** The channels 4ocean publishes on, matching platforms.ts. */
const PLATFORM_CHOICES = ['Instagram', 'TikTok', 'YouTube', 'Facebook'] as const;

export function usePushTaskToSocial(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      platforms: string[];
      type: string;
      pillar: string;
    }) => {
      const { data, error } = await client.rpc('push_task_to_social', {
        p_task_id: input.taskId,
        p_platforms: input.platforms,
        p_type: input.type,
        p_pillar: input.pillar,
      });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    onSuccess: () => {
      // Both ends changed: the task gained a link, Social Studio gained a post.
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      void queryClient.invalidateQueries({ queryKey: socialKeys.all });
    },
  });
}

export function pushErrorMessage(raw: string): string {
  if (raw.includes('task_already_pushed')) {
    return 'This task has already been pushed to Social Studio.';
  }
  if (raw.includes('only_project_tasks_can_be_pushed')) {
    return 'Only project tasks can become social posts.';
  }
  if (raw.includes('not_authorized_for_social_studio')) {
    return 'You need access to Social Studio to push a task there.';
  }
  if (raw.includes('not_authorized_to_push_task')) {
    return 'You need edit access to this project to push its tasks.';
  }
  return 'The task could not be pushed. Try again.';
}

export function PushToSocialDialog({
  task,
  projectId,
  onClose,
  onPushed,
}: {
  task: Task;
  projectId: string;
  onClose: () => void;
  onPushed: () => void;
}) {
  const push = usePushTaskToSocial(projectId);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [type, setType] = useState('');
  const [pillar, setPillar] = useState('');

  function toggle(platform: string) {
    setPlatforms((current) =>
      current.includes(platform)
        ? current.filter((entry) => entry !== platform)
        : [...current, platform],
    );
  }

  function submit() {
    push.mutate(
      { taskId: task.id, platforms, type: type.trim(), pillar: pillar.trim() },
      { onSuccess: onPushed },
    );
  }

  return (
    <Dialog
      open
      title="Push to Social Studio"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={push.isPending || platforms.length === 0}>
            {push.isPending ? 'Pushing…' : 'Create the post'}
          </Button>
        </>
      }
    >
      <p className="task-panel-text">
        Creates a Social Studio post from this task. The title, due date, assignee, description and
        attached links come across; the post stays an idea until somebody schedules it.
      </p>

      <fieldset className="push-platforms">
        <legend className="ui-label">Platforms</legend>
        {PLATFORM_CHOICES.map((platform) => (
          <Checkbox
            key={platform}
            label={platform}
            checked={platforms.includes(platform)}
            onChange={() => toggle(platform)}
          />
        ))}
      </fieldset>

      <Input
        label="Post type"
        hint="Reel, carousel, static — whatever you call it."
        value={type}
        onChange={(event) => setType(event.target.value)}
      />
      <Input
        label="Pillar"
        hint="Optional. Leave blank if it does not belong to one yet."
        value={pillar}
        onChange={(event) => setPillar(event.target.value)}
      />

      {push.isError ? <InlineError>{pushErrorMessage(push.error.message)}</InlineError> : null}
    </Dialog>
  );
}
